import { motion } from 'framer-motion';
import { Mail, MapPin, Clock, MessageSquare } from 'lucide-react';
import { ContactForm } from '../components/forms/ContactForm';
import { SEOHead } from '../components/SEOHead';

// Warm, editorial hero — an AMS artisan vendor at a farmers-market booth
// with handmade jams and baked goods. Uploaded by the client.
const HERO_IMAGE =
  'https://customer-assets-cm19k8pv.emergentagent.net/job_7adffd06-a6e7-4f95-8888-9b22d8068cd7/artifacts/u809aanj_Baker%20with%20Artisan%20Goods.png';

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'info@artisanmarketandstudio.com',
    href: 'mailto:info@artisanmarketandstudio.com',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Loudoun County, Virginia',
    href: null,
  },
  {
    icon: Clock,
    label: 'Response time',
    value: '24–48 hours',
    href: null,
  },
];

export default function ContactPage() {
  return (
    <div className="page-transition editorial" data-testid="contact-page">
      <SEOHead page="contact" />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="An AMS artisan vendor smiling at her farmers-market booth with handmade jams and baked goods"
            className="w-full h-full object-cover"
            data-testid="contact-hero-image"
          />
          {/* Bottom-weighted scrim — image reads at top, text is crisp at bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E3227]/95 from-0% via-[#1E3227]/70 via-40% to-[#1E3227]/20 to-95%" />
          {/* Feather into cream */}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[color:var(--ed-cream)]" />
        </div>

        <div
          className="relative max-w-5xl mx-auto px-6 pt-28 pb-20 md:pt-36 md:pb-28 min-h-[58vh] flex flex-col justify-end text-[#FDF7EA]"
          style={{ textShadow: '0 2px 16px rgba(0,0,0,0.7), 0 1px 3px rgba(0,0,0,0.5)' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[color:var(--ed-gold)]"
              data-testid="contact-hero-kicker"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Get in touch
            </span>
            <h1
              className="mt-4 text-5xl md:text-7xl font-editorial leading-[1.02] max-w-3xl"
              data-testid="contact-hero-title"
            >
              We&apos;d love to hear from you.
            </h1>
            <p className="mt-5 font-body text-lg md:text-xl max-w-2xl text-[#FDF7EA]/90">
              Questions about our events, becoming a vendor, hosting a market, or the Compass
              trial? Send a note and we&apos;ll write back personally.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28 bg-[color:var(--ed-cream)]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Left column — details */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <div className="flex items-center mb-6">
                <span className="rule" />
                <span className="kicker">Let&apos;s connect</span>
              </div>
              <h2 className="text-4xl leading-tight text-[color:var(--ed-forest-dark)] font-editorial">
                Small notes, real replies.
              </h2>
              <p className="mt-4 font-body text-[color:var(--ed-muted)]">
                Every message lands in a real inbox &mdash; not a ticketing queue. We usually reply
                within a day or two.
              </p>

              <div className="mt-10 space-y-5">
                {contactInfo.map((info) => (
                  <div
                    key={info.label}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${info.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="w-11 h-11 rounded-full bg-[color:var(--ed-terracotta)]/12 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-[color:var(--ed-terracotta)]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[color:var(--ed-muted)] mb-1">
                        {info.label}
                      </p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-[color:var(--ed-forest-dark)] font-medium hover:text-[color:var(--ed-terracotta)] transition-colors font-body"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-[color:var(--ed-forest-dark)] font-medium font-body">
                          {info.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* FAQ Teaser */}
              <div className="mt-10 card-warm">
                <p className="kicker">Common questions</p>
                <ul className="mt-4 space-y-2 font-body text-[color:var(--ed-forest-dark)]/80">
                  <li>&mdash; How do I become a vendor?</li>
                  <li>&mdash; What are the membership fees?</li>
                  <li>&mdash; How can I host an event?</li>
                  <li>&mdash; Where can I find event schedules?</li>
                </ul>
                <p className="mt-4 font-body text-sm text-[color:var(--ed-muted)]">
                  Send us a message &mdash; we&apos;ll walk you through it.
                </p>
              </div>
            </motion.div>

            {/* Right column — form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="bg-white border border-[color:var(--ed-forest)]/15 rounded-2xl p-8 md:p-10 shadow-sm">
                <div className="flex items-center mb-6">
                  <span className="rule" />
                  <span className="kicker">Write to us</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-editorial text-[color:var(--ed-forest-dark)] mb-6 leading-tight">
                  Send us a message
                </h2>
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
